/**
 * Pacific-time date rendering for Butler event metadata.
 *
 * Butler event data arrives in two shapes:
 *
 *   1. Date-only ISO strings ("YYYY-MM-DD") from `/api/events` when the
 *      backend is on DASHBOARD_PG and reads `event_metadata.start_date`
 *      (a pg DATE column — no time, no tz). We MUST treat these as
 *      Pacific wall-clock dates: calling `new Date("2026-04-23")` parses
 *      as UTC midnight, which in Pacific renders as the prior day.
 *
 *   2. Full ISO timestamps with explicit offset (e.g. "2026-05-04T23:59:59-07:00")
 *      from the end-of-day helper in pg-events.js. These are safe to
 *      parse with `new Date(...)` and render via `Intl` with an explicit
 *      `timeZone: 'America/Los_Angeles'`.
 *
 * This module centralizes both branches so every caller lands on the
 * same "Pacific date" regardless of the browser's local TZ. Ref:
 * ~/.claude/projects/-Users-BriansPc/memory/reference_butler_datetime_utc_convention.md
 */

export const PACIFIC_TZ = 'America/Los_Angeles'

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

interface PacificParts {
  year: number
  month: number // 1-12
  day: number   // 1-31
}

/**
 * Extract the Pacific calendar (year, month, day) from either a
 * date-only string ("YYYY-MM-DD") or a full ISO instant. Returns null
 * on unparseable input.
 */
export function pacificPartsFromIso(input?: string | null): PacificParts | null {
  if (!input || typeof input !== 'string') return null
  const trimmed = input.trim()
  if (!trimmed) return null

  const dateOnly = DATE_ONLY_RE.exec(trimmed)
  if (dateOnly) {
    // Date-only: treat as a wall-clock calendar date. No TZ conversion.
    const year = Number(dateOnly[1])
    const month = Number(dateOnly[2])
    const day = Number(dateOnly[3])
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
    return { year, month, day }
  }

  // Midnight-UTC event dates (e.g. "2026-05-01T00:00:00Z" or
  // "2026-05-01T00:00:00.000Z") are Butler's legacy calendar-date
  // convention under the DASHBOARD_LIVE path — the time component is a
  // placeholder, not a real instant. If we projected those through Pacific
  // we'd render the prior calendar day. Strip the time part and treat as
  // a wall-clock calendar date. Any non-midnight time is a real instant.
  const midnightUtc = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.0+)?Z$/.exec(trimmed)
  if (midnightUtc) {
    const year = Number(midnightUtc[1])
    const month = Number(midnightUtc[2])
    const day = Number(midnightUtc[3])
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
    return { year, month, day }
  }

  // Full ISO instant with a real time: parse then project into Pacific.
  const d = new Date(trimmed)
  if (Number.isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  let year = NaN
  let month = NaN
  let day = NaN
  for (const p of parts) {
    if (p.type === 'year') year = Number(p.value)
    else if (p.type === 'month') month = Number(p.value)
    else if (p.type === 'day') day = Number(p.value)
  }
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
  return { year, month, day }
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Format a Butler event date as "Mon D" in Pacific. Accepts either a
 * date-only "YYYY-MM-DD" string or a full ISO instant. Returns ''
 * on null/undefined/unparseable input so callers can `&&` the result
 * into their JSX safely.
 */
export function formatPacificMonthDay(input?: string | null): string {
  const parts = pacificPartsFromIso(input)
  if (!parts) return ''
  const mName = MONTH_SHORT[parts.month - 1]
  if (!mName) return ''
  return `${mName} ${parts.day}`
}

/**
 * Format a Butler event date as "Mon D, YYYY" in Pacific.
 */
export function formatPacificLongDate(input?: string | null): string {
  const parts = pacificPartsFromIso(input)
  if (!parts) return ''
  const mName = MONTH_SHORT[parts.month - 1]
  if (!mName) return ''
  return `${mName} ${parts.day}, ${parts.year}`
}

/**
 * Format a Butler event date range in Pacific.
 *
 *   - `start` only            → "Apr 23"
 *   - same month + year       → "Apr 23–25"
 *   - cross-month or cross-yr → "Apr 30 – May 2"
 */
export function formatPacificDateRange(
  start?: string | null,
  end?: string | null,
): string {
  const s = pacificPartsFromIso(start)
  if (!s) return ''
  const e = pacificPartsFromIso(end)
  if (!e) return formatPacificMonthDay(start)
  const sameMonth = s.month === e.month && s.year === e.year
  if (sameMonth) {
    const mName = MONTH_SHORT[s.month - 1]
    if (!mName) return ''
    return `${mName} ${s.day}–${e.day}`
  }
  return `${formatPacificMonthDay(start)} – ${formatPacificMonthDay(end)}`
}
